import React, { useState, useEffect } from 'react';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';

const Contact = () => {
  const [contacts, setContacts] = useState({});
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerReady, setBannerReady] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', enquiry_type: 'Contact Enquiry' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/contacts/public').then(res => {
      if (res.data.data) setContacts(res.data.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/pages/public/contact-us').then(res => {
      if (res.data?.data?.banner_image) setBannerImage(res.data.data.banner_image);
    }).catch(() => {}).finally(() => setBannerReady(true));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post('/enquiries/public', form);
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '', enquiry_type: 'Contact Enquiry' });
    } catch (err) { alert('Error sending message. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SEO slug="contact" title="Contact Us" description="Get in touch with St. Andrews World School, Siddharth Vihar. Find our address, phone number, email, and send us a message." />
      <PageBanner title="Contact Us" subtitle="Get in touch with St. Andrews World School, Siddharth Vihar" breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Contact Us' }]} backgroundImage={bannerImage ? `${UPLOADS_URL}/${bannerImage}` : null} fallback={`${process.env.PUBLIC_URL}/images/page-banners/contact-us.jpg`} loading={!bannerReady} />

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div>
              <h2 className="text-[28px] text-primary mb-6">Get in Touch</h2>
              {submitted ? (
                <div className="text-center p-10 bg-light rounded-md">
                  <i className="fas fa-check-circle text-[48px] text-green-500 mb-4"></i>
                  <h3 className="text-primary">Thank You!</h3>
                  <p className="text-body text-center">Your message has been sent successfully. We'll get back to you soon.</p>
                  <button className="btn mt-4" onClick={() => setSubmitted(false)}>Send Another Message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="form-group">
                    <label>Your Name *</label>
                    <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Enter your full name"
                      className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="Enter your email"
                      className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required placeholder="Enter your phone number"
                      className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                  </div>
                  
                  <div className="form-group">
                    <label>Message *</label>
                    <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} required placeholder="Write your message here..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary resize-y" />
                  </div>
                  <button type="submit" disabled={loading} className="btn">{loading ? 'Sending...' : 'Send Message'}</button>
                </form>
              )}
            </div>

            <div className="contact-info-list">
              <h3 className="text-primary">Contact Information</h3>
              <div className="contact-info-item">
                <div className="icon"><i className="fas fa-map-marker-alt"></i></div>
                <div>
                  <h4>Address</h4>
                  <p>{contacts.address || 'P.S.-01, Sector-09, Siddharth Vihar Ghaziabad – 201009'}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="icon"><i className="fas fa-phone"></i></div>
                <div>
                  <h4>Phone</h4>
                  <p><a href="tel:+919311668863" className="hover:text-primary">+91-9311668863</a></p>
                  <p><a href="tel:+919311668864" className="hover:text-primary">+91-9311668864</a></p>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="icon"><i className="fas fa-envelope"></i></div>
                <div>
                  <h4>Email</h4>
                  <p><a href="mailto:Info@sawssiddharthvihar.in" className="hover:text-primary">Info@sawssiddharthvihar.in</a></p>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="icon"><i className="fas fa-clock"></i></div>
                <div>
                  <h4>Working Hours</h4>
                  <p>{contacts.working_hours || 'Mon - Sat: 7:30 AM - 3:00 PM'}</p>
                </div>
              </div>
              <div>
                <h4 className="mt-5 mb-3">Follow Us</h4>
                <div className="flex gap-3">
                  <a href="https://www.facebook.com/share/1LWoXSJynp/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary text-lg transition-all duration-300 hover:bg-primary hover:text-white"><i className="fab fa-facebook-f"></i></a>
                  <a href="https://www.instagram.com/saws.siddharthvihar?igsh=MXNzbGd4eWNpZXRmeA%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary text-lg transition-all duration-300 hover:bg-primary hover:text-white"><i className="fab fa-instagram"></i></a>
                  <a href="https://youtube.com/channel/UCN-rxHSuiW_R7rPaTuvb5dw" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary text-lg transition-all duration-300 hover:bg-primary hover:text-white"><i className="fab fa-youtube"></i></a>
                </div>
              </div>
            </div>
          </div>

          {contacts.map_embed && (
            <div className="mt-10 rounded-md overflow-hidden">
              <div dangerouslySetInnerHTML={{ __html: contacts.map_embed }} />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Contact;
